import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Utils from "../../utils";

import { getLotDetails } from "../../services/LotsServices";
import {
  getCatalogList,
  actionPartInventoryTempStore,
  getPartInventoryTempList,
  DestoryPartInventoryTemp,
} from "../../services/PartsInventory";

// ============================================================
// AddForLotParts — Step B do flow "Adicionar a Lote".
// Paridade com mobile: part_inventory_screen.dart (PartInventoryListScreen
// em modo lote) + part_inventory_controller.apiCallForLotPartCatalogAdd.
//
// Vem de PartsInventory/index.js (modal lot picker) com state:
//   { lot_id, lot_name }
//
// Funções:
//   1. Carrega vehicles do lote via /lot/details — pré-selecciona default
//      (is_default=1) ou primeiro.
//   2. User pode trocar viatura (header).
//   3. Botão "Adicionar Peças do Catálogo" abre picker de categoria.
//      Categoria seleccionada → se is_master_part === "1", abre lista de
//      sub-peças com checkboxes; senão adiciona directo. Cada peça vai a
//      /vehicle-inventory-temp-store com o vehicle context da viatura
//      actualmente seleccionada.
//   4. Lista de peças temp já adicionadas (delete por linha).
//   5. "Continuar" → /parts-inventory-addparts com state
//      { data:{ temp_id }, lot_id, lot_name } para o user finalizar
//      (preço/qtd/fotos/danos) na página existente.
// ============================================================

const labelVehicle = (v) => {
  if (!v) return "—";
  const isLite = !v.vehicle_id || v.vehicle_id === 0;
  if (!isLite) {
    const internal = (v.vehicle_internal_id || "").trim();
    const plate = (v.registration_plate || "").trim();
    const reg = (v.registration || "").trim();
    const main = plate || reg || v.vehicle_make_name || "";
    return internal ? `#${internal} · ${main}` : main || "—";
  }
  const parts = [
    v.vehicle_make_name,
    v.vehicle_model_name,
    v.vehicle_year,
  ].filter((x) => (x || "").toString().trim().length);
  return parts.length ? parts.join(" · ") : "—";
};

const effectiveContext = (v) => {
  if (!v) return null;
  const isLite = !v.vehicle_id || v.vehicle_id === 0;
  return {
    isLite,
    vehicle_type_id: isLite ? v.vehicle_type_id : v.vehicle_type_real,
    vehicle_year: isLite ? v.vehicle_year : v.vehicle_year_real,
    vehicle_make_id: isLite ? v.vehicle_make_id : v.vehicle_make_real,
    vehicle_model_id: isLite ? v.vehicle_model_id : v.vehicle_model_real,
    vehicle_variant_id: isLite ? v.variants_id : v.vehicle_variant_real,
    vehicle_id: isLite ? 0 : v.vehicle_id || 0,
  };
};

const AddForLotParts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const lotIdFromState = location?.state?.lot_id || null;
  const lotNameFromState = location?.state?.lot_name || "";

  // Sem lot_id → redirect para listagem (estado inválido).
  useEffect(() => {
    if (!lotIdFromState) navigate("/parts-inventory");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loadingLot, setLoadingLot] = useState(true);
  const [lotVehicles, setLotVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);

  // Catalog category picker
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  // Sub-parts picker (when category is master)
  const [subPartsPickerOpen, setSubPartsPickerOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [subPartsSelected, setSubPartsSelected] = useState({});

  // Temp store
  const [tempId, setTempId] = useState("");
  const [tempItems, setTempItems] = useState([]);
  const [tempLoading, setTempLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // ---- Lot details ----
  useEffect(() => {
    if (!lotIdFromState) return;
    setLoadingLot(true);
    getLotDetails({ id: lotIdFromState })
      .then((res) => {
        const data = res?.data?.data || {};
        const vehicles = data.vehicles || [];
        setLotVehicles(vehicles);
        if (vehicles.length) {
          const def =
            vehicles.find(
              (v) => v.is_default === 1 || v.is_default === "1"
            ) || vehicles[0];
          setSelectedVehicle(def);
        }
        setLoadingLot(false);
      })
      .catch((err) => {
        setLoadingLot(false);
        toast.error(Utils.getErrorMessage(err));
      });
  }, [lotIdFromState]);

  // ---- Category picker (catalog) ----
  const loadCategories = (search, vehicleTypeId) => {
    setCategoryLoading(true);
    getCatalogList({
      search: search || "",
      limit: 50,
      page: 1,
      vehicle_type_id: vehicleTypeId || 0,
    })
      .then((res) => {
        setCategoryList(res?.data?.data || []);
        setCategoryLoading(false);
      })
      .catch(() => {
        setCategoryList([]);
        setCategoryLoading(false);
      });
  };

  const openCategoryPicker = () => {
    if (!selectedVehicle) {
      toast.warning(
        t("please_select_vehicle", { defaultValue: "Selecciona uma viatura" })
      );
      return;
    }
    setCategoryPickerOpen(true);
    setCategorySearch("");
    const ctx = effectiveContext(selectedVehicle);
    loadCategories("", ctx?.vehicle_type_id);
  };

  const onCategorySearchChange = (val) => {
    setCategorySearch(val);
    const ctx = effectiveContext(selectedVehicle);
    loadCategories(val, ctx?.vehicle_type_id);
  };

  const onPickCategory = (cat) => {
    setCategoryPickerOpen(false);
    if (cat.is_master_part === "1" || cat.is_master_part === 1) {
      setActiveCategory(cat);
      const initial = {};
      (cat.part_list || []).forEach((p) => {
        initial[p.id] = false;
      });
      setSubPartsSelected(initial);
      setSubPartsPickerOpen(true);
    } else {
      // Categoria simples — adicionar directo.
      submitTempAdd(cat, []);
    }
  };

  // ---- Submit category (and optional sub-parts) to temp store ----
  const submitTempAdd = (cat, subPartIds) => {
    const ctx = effectiveContext(selectedVehicle);
    if (!ctx) {
      toast.warning(
        t("please_select_vehicle", { defaultValue: "Selecciona uma viatura" })
      );
      return;
    }

    const part_list = [
      {
        catalog_id: cat.id,
        vehicle_type_id: ctx.vehicle_type_id || 0,
        vehicle_year: ctx.vehicle_year || "",
        vehicle_make: ctx.vehicle_make_id || 0,
        vehicle_model: ctx.vehicle_model_id || 0,
        vehicle_variant: ctx.vehicle_variant_id || 0,
        master_part_id: "0",
        vehicle_id: ctx.vehicle_id || 0,
      },
      ...((cat.part_list || [])
        .filter((p) => subPartIds.includes(p.id))
        .map((p) => ({
          catalog_id: p.id,
          vehicle_type_id: ctx.vehicle_type_id || 0,
          vehicle_year: ctx.vehicle_year || "",
          vehicle_make: ctx.vehicle_make_id || 0,
          vehicle_model: ctx.vehicle_model_id || 0,
          vehicle_variant: ctx.vehicle_variant_id || 0,
          master_part_id: String(cat.id),
          vehicle_id: ctx.vehicle_id || 0,
        }))),
    ];

    const payload = {
      temp_id: tempId || "",
      vehicle_id: 0, // skip dedup global — paridade com mobile.
      part_list,
    };

    setBusy(true);
    actionPartInventoryTempStore(payload)
      .then((res) => {
        const data = res?.data?.data;
        const newTempId = data?.temp_id || tempId;
        setTempId(newTempId);
        toast.success(
          res?.data?.message ||
            t("toast_added", { defaultValue: "Adicionado" })
        );
        setSubPartsPickerOpen(false);
        loadTempList(newTempId);
        setBusy(false);
      })
      .catch((err) => {
        setBusy(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const loadTempList = (idOverride) => {
    const id = idOverride || tempId;
    if (!id) {
      setTempItems([]);
      return;
    }
    setTempLoading(true);
    getPartInventoryTempList({
      search: "",
      limit: 100,
      page: 1,
      temp_id: id,
    })
      .then((res) => {
        setTempItems(res?.data?.data || []);
        setTempLoading(false);
      })
      .catch(() => {
        setTempItems([]);
        setTempLoading(false);
      });
  };

  const onDeleteTempItem = (item) => {
    setBusy(true);
    DestoryPartInventoryTemp({ id: item.id })
      .then((res) => {
        toast.success(
          res?.data?.message ||
            t("deleted", { defaultValue: "Removido" })
        );
        loadTempList();
        setBusy(false);
      })
      .catch((err) => {
        setBusy(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  // ---- Continue button ----
  const onContinue = () => {
    if (!tempId || tempItems.length === 0) {
      toast.warning(
        t("please_select_part_catalog", {
          defaultValue: "Adiciona pelo menos uma peça",
        })
      );
      return;
    }
    navigate("/parts-inventory-addparts", {
      state: {
        data: { temp_id: tempId },
        lot_id: lotIdFromState,
        lot_name: lotNameFromState,
      },
    });
  };

  // ---- Render ----
  const subPartsList = useMemo(
    () => activeCategory?.part_list || [],
    [activeCategory]
  );

  const numSelectedSub = useMemo(
    () =>
      Object.values(subPartsSelected || {}).filter((v) => v === true).length,
    [subPartsSelected]
  );

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1
                style={{ fontSize: 18, color: "#203444", fontWeight: 700 }}
              >
                <i
                  className="bi bi-archive-fill me-2"
                  style={{ color: "#81BB42" }}
                />
                {t("add_for_lot", { defaultValue: "Adicionar a Lote" })}
                {": "}
                {lotNameFromState || `#${lotIdFromState}`}
              </h1>
            </div>
            <div className="col-sm-6 text-end">
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate("/parts-inventory")}
              >
                <i className="bi bi-x-lg me-1" />
                {t("cancel", { defaultValue: "Cancelar" })}
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={busy || !tempId || tempItems.length === 0}
                onClick={onContinue}
              >
                {t("continue", { defaultValue: "Continuar" })}
                <i className="bi bi-chevron-right ms-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  {/* Vehicle picker */}
                  <div
                    className="d-flex align-items-center p-3 mb-3"
                    style={{
                      border: "2px solid #81BB42",
                      borderRadius: 12,
                      backgroundColor: "#fff",
                    }}
                  >
                    <i
                      className="bi bi-truck-front-fill me-3"
                      style={{ color: "#81BB42", fontSize: 28 }}
                    />
                    <div className="flex-grow-1">
                      <div
                        style={{ fontSize: 12, color: "#6c757d" }}
                      >
                        {t("select_lot_vehicle", {
                          defaultValue: "Viatura do lote",
                        })}
                      </div>
                      <div
                        style={{ fontSize: 16, fontWeight: 600, color: "#203444" }}
                      >
                        {loadingLot
                          ? `${t("loading", { defaultValue: "A carregar" })}...`
                          : selectedVehicle
                            ? labelVehicle(selectedVehicle)
                            : t("not_selected", {
                                defaultValue: "Nenhuma seleccionada",
                              })}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      disabled={loadingLot || lotVehicles.length === 0}
                      onClick={() => setVehiclePickerOpen(true)}
                    >
                      <i className="bi bi-arrow-left-right me-1" />
                      {t("change", { defaultValue: "Trocar" })}
                    </button>
                  </div>

                  {!loadingLot && lotVehicles.length === 0 && (
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2" />
                      {t("lot_no_vehicles", {
                        defaultValue:
                          "Este lote não tem viaturas associadas. Adiciona uma viatura no detalhe do lote.",
                      })}
                    </div>
                  )}

                  {/* Add parts button */}
                  <button
                    type="button"
                    className="btn w-100 d-flex align-items-center justify-content-center py-3"
                    disabled={!selectedVehicle || busy}
                    onClick={openCategoryPicker}
                    style={{
                      backgroundColor: selectedVehicle ? "#81BB42" : "#e9ecef",
                      color: selectedVehicle ? "#fff" : "#6c757d",
                      fontWeight: 600,
                      borderRadius: 12,
                      fontSize: 15,
                    }}
                  >
                    <i className="bi bi-plus-square-fill me-2" />
                    {t("select_parts", {
                      defaultValue: "Adicionar peças do catálogo",
                    })}
                  </button>

                  {/* Temp items list */}
                  <div className="mt-4">
                    <div
                      className="d-flex align-items-center justify-content-between mb-2"
                    >
                      <h5
                        style={{ color: "#203444", fontWeight: 600, margin: 0 }}
                      >
                        {t("added_parts", { defaultValue: "Peças adicionadas" })}{" "}
                        ({tempItems.length})
                      </h5>
                      {tempLoading && (
                        <span className="text-muted" style={{ fontSize: 13 }}>
                          <i className="bi bi-arrow-clockwise" />{" "}
                          {t("loading", { defaultValue: "A carregar" })}...
                        </span>
                      )}
                    </div>

                    {tempItems.length === 0 ? (
                      <div
                        className="text-center text-muted py-4"
                        style={{
                          border: "1px dashed #dee2e6",
                          borderRadius: 8,
                        }}
                      >
                        {t("no_parts_found", {
                          defaultValue: "Ainda não adicionaste peças.",
                        })}
                      </div>
                    ) : (
                      <div className="list-group">
                        {tempItems.map((item) => (
                          <div
                            key={item.id}
                            className="list-group-item d-flex align-items-center"
                          >
                            <i
                              className="bi bi-gear-fill me-3"
                              style={{ color: "#81BB42", fontSize: 18 }}
                            />
                            <div className="flex-grow-1">
                              <div style={{ fontWeight: 600, color: "#203444" }}>
                                {item.namePartPT ||
                                  item.name_part_pt ||
                                  item.namePart ||
                                  "—"}
                              </div>
                              <div
                                style={{ fontSize: 12, color: "#6c757d" }}
                              >
                                {(item.vehicle_make_name ||
                                  item.vehicleMakeName ||
                                  "")}
                                {item.vehicle_model_name || item.vehicleModelName
                                  ? ` · ${item.vehicle_model_name || item.vehicleModelName}`
                                  : ""}
                                {item.vehicle_year ? ` · ${item.vehicle_year}` : ""}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger"
                              disabled={busy}
                              onClick={() => onDeleteTempItem(item)}
                              title={t("delete", { defaultValue: "Remover" })}
                            >
                              <i className="bi bi-trash fs-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle picker modal */}
      <Modal
        isOpen={vehiclePickerOpen}
        toggle={() => setVehiclePickerOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setVehiclePickerOpen(false)}>
          {t("select_lot_vehicle", { defaultValue: "Viatura do lote" })}
        </ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {lotVehicles.map((v) => {
              const isSelected = selectedVehicle?.id === v.id;
              const isLite = !v.vehicle_id || v.vehicle_id === 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  className="list-group-item list-group-item-action text-start w-100 d-flex align-items-center"
                  onClick={() => {
                    setSelectedVehicle(v);
                    setVehiclePickerOpen(false);
                  }}
                  style={{
                    border: 0,
                    borderBottom: "1px solid #e9ecef",
                    padding: "12px 14px",
                    backgroundColor: isSelected ? "#eef7e2" : "#fff",
                  }}
                >
                  <i
                    className={
                      isLite
                        ? "bi bi-clipboard-check me-2"
                        : "bi bi-truck-front me-2"
                    }
                    style={{
                      color: isSelected ? "#81BB42" : "#6c757d",
                      fontSize: 18,
                    }}
                  />
                  <div className="flex-grow-1">
                    <div style={{ fontWeight: 600, color: "#203444" }}>
                      {labelVehicle(v)}
                    </div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      {isLite
                        ? t("lite_vehicle", { defaultValue: "Viatura lite" })
                        : t("existing_vehicle", {
                            defaultValue: "Viatura existente",
                          })}
                      {v.is_default === 1 || v.is_default === "1"
                        ? ` · ${t("default", { defaultValue: "Predefinida" })}`
                        : ""}
                    </div>
                  </div>
                  {isSelected && (
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ color: "#81BB42", fontSize: 20 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </ModalBody>
      </Modal>

      {/* Category picker modal */}
      <Modal
        isOpen={categoryPickerOpen}
        toggle={() => setCategoryPickerOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setCategoryPickerOpen(false)}>
          {t("select_part_catalog", {
            defaultValue: "Selecciona categoria de peça",
          })}
        </ModalHeader>
        <ModalBody>
          <input
            type="text"
            className="form-control mb-2"
            placeholder={t("search", { defaultValue: "Pesquisar..." })}
            value={categorySearch}
            onChange={(e) => onCategorySearchChange(e.target.value)}
            autoFocus
          />
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {categoryLoading && (
              <div className="text-center text-muted py-3">
                <i className="bi bi-arrow-clockwise" /> ...
              </div>
            )}
            {!categoryLoading && categoryList.length === 0 && (
              <div className="text-center text-muted py-3">
                {t("no_data_found", { defaultValue: "Sem resultados" })}
              </div>
            )}
            {!categoryLoading &&
              categoryList.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className="list-group-item list-group-item-action text-start w-100 d-flex align-items-center"
                  onClick={() => onPickCategory(cat)}
                  style={{
                    border: 0,
                    borderBottom: "1px solid #e9ecef",
                    padding: "10px 12px",
                  }}
                >
                  <i
                    className="bi bi-tag-fill me-2"
                    style={{ color: "#81BB42" }}
                  />
                  <span className="flex-grow-1">{cat.namePartPT || "—"}</span>
                  {(cat.is_master_part === "1" || cat.is_master_part === 1) && (
                    <span
                      className="badge bg-light text-muted me-2"
                      style={{ fontSize: 11 }}
                    >
                      {(cat.part_list || []).length}
                    </span>
                  )}
                  <i className="bi bi-chevron-right text-muted" />
                </button>
              ))}
          </div>
        </ModalBody>
      </Modal>

      {/* Sub-parts picker modal */}
      <Modal
        isOpen={subPartsPickerOpen}
        toggle={() => setSubPartsPickerOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setSubPartsPickerOpen(false)}>
          {activeCategory?.namePartPT || "—"}
        </ModalHeader>
        <ModalBody>
          {selectedVehicle && (
            <div
              className="mb-2"
              style={{ fontSize: 13, color: "#6c757d", fontWeight: 600 }}
            >
              {labelVehicle(selectedVehicle)}
            </div>
          )}
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {subPartsList.length === 0 && (
              <div className="text-center text-muted py-3">
                {t("no_data_found", { defaultValue: "Sem sub-peças" })}
              </div>
            )}
            {subPartsList.map((p) => {
              const checked = !!subPartsSelected[p.id];
              return (
                <label
                  key={p.id}
                  className="d-flex align-items-center"
                  style={{
                    cursor: "pointer",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e9ecef",
                    backgroundColor: checked ? "#eef7e2" : "transparent",
                    borderRadius: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={checked}
                    onChange={(e) =>
                      setSubPartsSelected((prev) => ({
                        ...prev,
                        [p.id]: e.target.checked,
                      }))
                    }
                  />
                  <span style={{ color: "#203444" }}>{p.namePartPT || "—"}</span>
                </label>
              );
            })}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() => setSubPartsPickerOpen(false)}
            >
              {t("cancel", { defaultValue: "Cancelar" })}
            </button>
            <button
              type="button"
              className="btn btn-success"
              disabled={busy || numSelectedSub === 0}
              onClick={() => {
                const ids = Object.entries(subPartsSelected)
                  .filter(([, v]) => v === true)
                  .map(([k]) => Number(k));
                submitTempAdd(activeCategory, ids);
              }}
            >
              <i className="bi bi-plus-lg me-1" />
              {t("add_selected", { defaultValue: "Adicionar selecionadas" })}
              {numSelectedSub > 0 ? ` (${numSelectedSub})` : ""}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default AddForLotParts;
